<template>
  <Teleport to="body">
    <div
      aria-modal="true"
      class="book-viewer"
      role="dialog"
      tabindex="-1"
      ref="root"
      :aria-label="label"
    >
      <button
        class="book-viewer__close"
        type="button"
        :aria-label="closeLabel"
        @click="close"
      >
        <SlideshowIcon name="close" />
      </button>
      <MemoryBookFlip
        class="book-viewer__book"
        size="large"
        ref="flip"
        :autoplay="{ allowed: true, manual: true, start: playing }"
        :book="book"
        :key-order="KEY_ORDER.viewer"
        :keys-active="() => true"
        :section-id="sectionId"
        :single="single"
        :start-spread="spread"
      />
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, ref } from "vue";
  import { useMediaQuery } from "../../composables/useSlideshow";
  import { KEY_ORDER } from "../../highlights/book-keys";
  import type { MemoryBook } from "../../highlights/memory-book";
  import { commonTexts } from "../../highlights/texts";
  import SlideshowIcon from "../Slideshow/SlideshowIcon.vue";
  import MemoryBookFlip from "./MemoryBookFlip.vue";

  /**
   * The book over the whole window, like the slideshow's viewer (PRO-4397). It opens on the
   * spread the section showed and gives it back on close. Its play button turns the pages on
   * request, and carries on at once when the section was turning by itself.
   */
  defineProps<{
    book: MemoryBook;
    label: string;
    /** The section was turning by itself. */
    playing: boolean;
    sectionId: string;
    spread: number;
  }>();

  const emit = defineEmits<{ close: [spread: number] }>();

  const closeLabel = commonTexts().close ?? "";
  /** A phone held upright: one page at a time. */
  const single = useMediaQuery(
    "(max-width: 699px) and (orientation: portrait)"
  );
  const root = ref<HTMLElement | null>(null);
  const flip = ref<InstanceType<typeof MemoryBookFlip> | null>(null);
  let returnFocus: HTMLElement | null = null;

  function close() {
    emit("close", flip.value?.spread ?? 0);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      close();
    }
  }

  onMounted(() => {
    returnFocus = document.activeElement as HTMLElement | null;
    window.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden";
    root.value?.focus({ preventScroll: true });
    if (
      root.value &&
      !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      root.value.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 250,
        easing: "ease-out",
      });
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKey);
    document.documentElement.style.overflow = "";
    returnFocus?.focus?.({ preventScroll: true });
  });
</script>
